/**
 * Formats a phone number to the standard +91 XXXXXXXXXX format
 * Handles various input formats:
 * - 9876543210 -> +91 9876543210
 * - 09876543210 -> +91 9876543210
 * - +919876543210 -> +91 9876543210
 * - 919876543210 -> +91 9876543210
 * - +91 9876543210 -> +91 9876543210 (already formatted)
 */
export function formatPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // Remove all non-digit characters except +
  let cleaned = phone.replace(/[^\d+]/g, '');
  
  // If already in correct format, return as-is
  if (/^\+91 \d{10}$/.test(phone)) {
    return phone;
  }
  
  // Remove + if present for easier processing
  if (cleaned.startsWith('+')) {
    cleaned = cleaned.substring(1);
  }
  
  // Remove leading 91 country code if present
  if (cleaned.startsWith('91') && cleaned.length >= 12) {
    cleaned = cleaned.substring(2);
  }
  
  // Remove leading 0 if present
  if (cleaned.startsWith('0') && cleaned.length === 11) {
    cleaned = cleaned.substring(1);
  }
  
  // If we have exactly 10 digits, format with +91 prefix
  if (cleaned.length === 10 && /^\d{10}$/.test(cleaned)) {
    return `+91 ${cleaned}`;
  }
  
  // Return original if doesn't match expected patterns
  return phone;
}

/**
 * Extracts the local number (last 10 digits) from any phone format
 */
export function extractLocalNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  
  // Extract only digits
  const digits = phone.replace(/\D/g, '');
  
  // Return last 10 digits
  if (digits.length > 10) {
    return digits.slice(-10);
  }
  
  return digits;
}

/**
 * Validates if a phone number has exactly 10 digits
 */
export function isValidPhoneLength(phone: string | null | undefined): boolean {
  const localNumber = extractLocalNumber(phone);
  return localNumber.length === 10;
}

/**
 * Validates if a phone number is in valid Indian format
 * Must have exactly 10 digits starting with 6-9
 */
export function isValidIndianPhone(phone: string | null | undefined): boolean {
  if (!phone) return false;
  const localNumber = extractLocalNumber(phone);
  return /^[6-9]\d{9}$/.test(localNumber);
}

/**
 * Normalizes phone number for comparison
 * Strips all formatting and returns last 10 digits
 */
export function normalizePhoneForComparison(phone: string | null | undefined): string {
  return extractLocalNumber(phone);
}

/**
 * Masks a phone number showing only the last 4 digits
 * +91 9876543210 → +91 ******3210
 */
export function maskPhoneNumber(phone: string | null | undefined): string {
  if (!phone) return '';
  const formatted = formatPhoneNumber(phone);
  
  // Handle +91 format
  if (/^\+91 \d{10}$/.test(formatted)) {
    return `+91 ******${formatted.slice(-4)}`;
  }
  
  // Generic masking - show last 4 digits
  const digits = phone.replace(/\D/g, '');
  if (digits.length >= 4) {
    return `******${digits.slice(-4)}`;
  }
  return '******';
}
